import { message } from "antd";
import { MediaPurpose } from "@/utils/enum";
import { startTrackingLoggedInUserForDatadog } from "@/utils/setupDatadog";
import { checkUserCanAddUsersToEntity } from "@/utils/utils";
import { getProfileData, updateProfileData, updateProfilePic } from "@/services/account";
import { getCompaniesAllowed } from "@/services/companies";
import { updateDocument } from "@/services/media";
import { createDealer, queryCurrent, query as queryUsers, updateEntityInfo } from "@/services/user";

const UserModel = {
  namespace: "user",
  state: {
    currentUser: {},
    currentProfile: {},
    userManager: {},
    dealers: [],
    notificationSettings: {},
    connectionRoleTypes: [],
  },
  effects: {
    *fetch(_, { call, put }) {
      const response = yield call(queryUsers);
      yield put({
        type: "save",
        payload: response,
      });
    },
    *fetchProfileData(_, { put, call }) {
      const response = yield call(getProfileData);
      yield put({
        type: "show",
        payload: response,
      });

      return {
        data: response,
      };
    },
    *updatePicProfile({ payload }, { call, put }) {
      try {
        const update = yield call(updateDocument, payload, MediaPurpose.ProfilePicture);
        yield call(updateProfilePic, {
          profilePhotoUri: update.url,
        });

        yield put({
          type: "setProfilePicture",
          profilePictureUrl: update.url,
        });

        return update.url;
      } catch (error) {
        message.error("Someting went wrong uploading the profile picture");
      }
    },
    *updateDataProfile({ payload }, { call, put }) {
      yield call(updateProfileData, payload);
      const response = yield call(getProfileData);

      yield put({
        type: "setProfileInfo",
        payload: response,
      });
    },
    *fetchCurrentProfile(_, { call, put, select }) {
      const response = yield call(getProfileData);
      yield put({
        type: "saveCurrentProfile",
        payload: response,
      });

      startTrackingLoggedInUserForDatadog(response);

      return yield select(state => state.user.currentProfile);
    },
    *fetchCurrent(_, { call, put, select }) {
      const userMngr = yield select(state => state.user.userManager);
      const response = yield call(queryCurrent, userMngr);
      if (response.ok === false || response.length === 0) {
        userMngr.signoutRedirect();
      }
      if (response[0].verified === false) {
        userMngr.signoutRedirect();
      }
      let dealers = response.map(user => user.entity);
      let connectedEntities = response.map(user => {
        return {
          ...user.entity,
          dealerRole: user.dealerRole,
          transporterRole: user.transporterRole,
        };
      });
      let connectionRoleTypes = response.map(user => {
        return {
          transporterRole: user.transporterRole,
          dealerRole: user.dealerRole,
          entityId: user.entity.entityId,
          type: user.entity.type,
        };
      });
      let notificationSettings = response[0].notificationSetting;
      yield put({
        type: "saveCurrentUser",
        payload: response[0],
        data: response, // temp, to get all entities of user
        dealers: dealers, // Deprecated property, leaving in to not break old components
        connectedEntities, // Reference off of this property instead, includes current role level in each entity
        connectionRoleTypes: connectionRoleTypes,
        notificationSettings: notificationSettings,
      });

      const currentUser = yield select(state => state.user.currentUser); // grab current user after save updates

      const urlParams = new URLSearchParams(window.location.search);
      const entityId = urlParams.get("entityId");
      let type = [];
      if (response[0].autosledAdminLevel !== 3) {
        type.push("noBDM");
      }
      if (entityId) {
        // if entity id exists check if it's a dealer or transporter
        for (let i = 0; i < dealers.length; i++) {
          if (entityId === dealers[i].entityId) {
            if (dealers[i].type === 1) {
              type.push("dealer");
              if (checkUserCanAddUsersToEntity(currentUser, connectionRoleTypes, dealers[i])) {
                type.push("canAddUsers");
              }
              break;
            }
            if (dealers[i].type === 0) {
              type.push("transporter");
              if (checkUserCanAddUsersToEntity(currentUser, connectionRoleTypes, dealers[i])) {
                type.push("canAddUsers");
              }
              break;
            }
          }
        }
      } else {
        // if no entity id check if their available enitity ids are dealers or transporters or both
        const hasRoleWithOneOrMoreDealerEntities = dealers.some(d => d.type === 1);
        if (hasRoleWithOneOrMoreDealerEntities) {
          type.push("dealer");
        }

        const hasRoleWithOneOrMoreTransporterEntities = dealers.some(d => d.type === 0);
        if (hasRoleWithOneOrMoreTransporterEntities) {
          type.push("transporter");
        }

        // Checking connected roles to determine if user has any entities that they can add users under
        const canAddUsersToOneOrMoreEntities = dealers.some(d =>
          checkUserCanAddUsersToEntity(currentUser, connectionRoleTypes, d),
        );
        if (canAddUsersToOneOrMoreEntities) {
          type.push("canAddUsers");
        }
      }

      let entities = response.map(user => user.entity);

      if (response[0].autosledAdminLevel === 1) {
        type.push("administrator");
      }
      if (response[0].autosledAdminLevel === 7) {
        type.push("opsManager");
      }
      // setAuthority(
      //   response[0].autosledAdminLevel ? ["admin", ...type] : ["user", ...type],
      // ); // set in localstorage what access the user has

      // Redirect if the user was given a temp password
      if (response[0].hasTempPassword && !window.location.href.includes("/change-temp-password")) {
        window.location = "/change-temp-password";
      }

      // returning currentUser to check autosledAdminLevel in src/layouts/BasicLayout.jsx to determine if the jira helpdesk widget should run
      return currentUser;
    },
    *saveUserManager({ payload }, { put }) {
      yield put({
        type: "saveCurrentUserManager",
        payload: payload.userManager,
      });
    },
    *saveBilling(_, { call, put, select }) {
      const userMngr = yield select(state => state.user.userManager);
      const response = yield call(queryCurrent, userMngr);
      yield put({
        type: "updateDealerInfo",
        dealers: response.map(user => user.entity),
      });
    },
    *updateEntity({ payload }, { call, put, select }) {
      const userMngr = yield select(state => state.user.userManager);
      yield call(updateEntityInfo, userMngr, payload);
      const response = yield call(queryCurrent, userMngr);
      yield put({
        type: "updateDealerInfo",
        dealers: response.map(user => user.entity),
      });
    },
    *addDealerEntity({ payload }, { call, put, select }) {
      const userMngr = yield select(state => state.user.userManager);
      yield call(createDealer, userMngr, payload);
      const response = yield call(queryCurrent, userMngr);
      yield put({
        type: "updateDealerInfo",
        dealers: response.map(user => user.entity),
      });
    },
    *fetchMyProfile({}, { call }) {
      const myCompanies = yield call(getCompaniesAllowed, {
        page: 1,
        pageSize: 20, // ask for pagination here
      });

      return {
        data: myCompanies?.items ?? [],
      };
    },
  },
  reducers: {
    updateProfile(state, action) {
      return {
        ...state,
        currentProfile: { ...state.currentProfile, ...action.payload },
      };
    },
    updateUser(state, action) {
      return {
        ...state,
        currentUser: { ...state.currentUser, ...action.payload },
      };
    },
    updateProfilePic(state, action) {
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          profilePictureCloudFrontUrl: action.profilePictureCloudFrontUrl,
        },
      };
    },
    updateDealerInfo(state, action) {
      return { ...state, dealers: action.dealers };
    },
    saveCurrentProfile(state, action) {
      return {
        ...state,
        currentProfile: action.payload || {},
      };
    },
    saveCurrentUser(state, action) {
      return {
        ...state,
        data: action.data || [],
        currentUser: action.payload || {},
        dealers: action.dealers,
        connectedEntities: action.connectedEntities,
        connectionRoleTypes: action.connectionRoleTypes,
        notificationSettings: action.notificationSettings,
      };
    },
    saveCurrentUserManager(state, { payload }) {
      return { ...state, userManager: payload || {} };
    },
    updateNotificationSettings(state, { payload }) {
      return {
        ...state,
        notificationSettings: { ...state.notificationSettings, ...payload },
      };
    },
    changeNotifyCount(
      state = {
        currentUser: {},
      },
      action,
    ) {
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          notifyCount: action.payload.totalCount,
          unreadCount: action.payload.unreadCount,
        },
      };
    },
    setProfileInfo(state, { payload }) {
      return {
        ...state,
        currentProfile: payload || {},
      };
    },
    setProfilePicture(state, { profilePictureUrl }) {
      return {
        ...state,
        currentProfile: {
          ...state.currentProfile,
          profilePhoto: profilePictureUrl,
        },
      };
    },
  },
};
export default UserModel;
